import { Route, RouteConfig } from 'vue-router';

import { AttachmentType } from '@/enums/attachmentType.enum';
import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { ModuleName } from '@/enums/moduleName.enum';
import { Options } from '@/enums/options.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { TableName } from '@/enums/tableName';
import store from '@/store/store';

const defaultTitle = 'Edycja wniosku';

export const documentEditRoute: RouteConfig = {
  name: 'documentEdit',
  path: 'edit',
  component: () => import(/* webpackChunkName: "DocumentEdit" */ '@/views/mainRoute/document/Edit.vue'),
  props: { edit: true },
  meta: { breadcrumb: { title: defaultTitle } },
  beforeEnter: (to, from, next) => {
    store.commit('document/setId', parseInt(to.params.documentId));
    store
      .dispatch('document/get')
      .then(document => {
        if (document.status.stage?.module?.name !== ModuleName.edit) {
          next(from);
          throw new Error(Errors.DOCUMENT_CANNOT_GET);
        }
        return document;
      })
      .then(document => [document.status.id, document.procedure.status.id])
      .then(([statusId, procedureStatusId]) =>
        Promise.all([
          getOptionValues(statusId, procedureStatusId),
          getSettings(to),
          getAttachments(to),
          getCriterions(statusId),
          getStudents()
        ])
      )
      .then(() => next())
      .catch(err => {
        store.dispatch('notifyError', { title: Errors.DOCUMENT_CANNOT_GET });
      });
  }
};

/**
 * Pobiera wartości opcji
 *
 * @param {number} statusId
 * @param {number} procedureStatusId
 * @returns {Promise<any>}
 */
function getOptionValues(statusId: number, procedureStatusId: number): Promise<any> {
  return Promise.all([
    store.dispatch('optionValue/getByCode', {
      code: Options.DICTIONARY,
      entityType: TableName.status,
      entityId: statusId.toString()
    }),
    store.dispatch('optionValue/getByCode', {
      code: Options.VALIDATE_ATTACHMENTS,
      entityType: TableName.status,
      entityId: statusId.toString()
    }),
    store
      .dispatch('optionValue/getByCode', {
        code: Options.SCHEMA_ID,
        entityType: TableName.status,
        entityId: statusId.toString()
      })
      .then(result => store.dispatch('schema/get', parseInt(result.value))),
    store.dispatch('optionValue/getByCode', {
      code: Options.TERM_PASSED,
      entityType: TableName.status,
      entityId: procedureStatusId.toString()
    })
  ]);
}

/**
 * Pobiera ustawienia
 *
 * @param {Route} route
 * @returns {Promise<any>}
 */
function getSettings(route: Route): Promise<any> {
  return Promise.all([
    store.dispatch('setting/findPublic', { code: PublicSettings.OW_003_MAX_AMOUNT }),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.ATTACHMENTS_SHOW_RADIO,
      entityType: TableName.procedure,
      entityId: route.params.procedureId.toString()
    }),
    store
      .dispatch('setting/findPublic', {
        code: PublicSettings.PROCEDURE_DOCUMENT_EDIT_BREADCRUMB,
        entityType: TableName.procedure,
        entityId: route.params.procedureId.toString()
      })
      .then(setting => {
        if (setting) {
          documentEditRoute.meta.breadcrumb.title = setting.value;
        } else {
          documentEditRoute.meta.breadcrumb.title = defaultTitle;
        }
      }),
    store.dispatch('setting/findPublic', { code: PublicSettings.OLD_STRUCTURE_NODE_MAPPING })
  ]);
}

/**
 * Pobiera załączniki
 *
 * @param {Route} route
 * @returns {Promise<any>}
 */
function getAttachments(route: Route): Promise<any> {
  return store
    .dispatch('document/attachment/list', {
      order: 'order',
      desc: false,
      count: -1,
      entityType: EntityType.DOCUMENT,
      entityId: route.params.documentId.toString(),
      type: AttachmentType.UPLOADED
    })
    .then(() => getChildrenAttachments());
}

/**
 * Pobiera załączniki dzieci
 *
 * @returns {Promise<any>}
 */
function getChildrenAttachments(): Promise<any> {
  const childrenIds = store.state.document.data.children.map((child: any) => child.id);

  return Promise.all(
    childrenIds.map((id: number) =>
      store.dispatch('document/attachment/listAppend', {
        order: 'order',
        desc: false,
        count: -1,
        entityType: EntityType.DOCUMENT,
        entityId: id.toString()
      })
    )
  );
}

/**
 * Pobiera kryteria
 *
 * @param {number} statusId
 * @returns {Promise<any>}
 */
function getCriterions(statusId: number): Promise<any> {
  return store.dispatch('criterion/list', { statusId, count: -1, order: 'order', desc: false });
}

/**
 * Pobiera uczniów
 *
 * @returns {Promise<any>}
 */
function getStudents(): Promise<any> {
  return store
    .dispatch('person/listForCurrentUser')
    .then(response => response?.list)
    .then(students => {
      const student = students?.length === 1 && students[0];
      if (student?.structureUid) {
        return store.dispatch('structureNode/getByExternalUid', student.structureUid);
      }
    });
}
